import React, { createContext, useContext, useState, useEffect } from 'react';
import { useContent } from './ContentContext';

// Hardcoded fallback translations (used when API is unavailable)
const defaultTranslations = {
  en: {
    nav: { home: 'Home', about: 'About Us', products: 'Products', news: 'News', contact: 'Contact' },
    hero: { overline: 'Established 1987 — A Legacy of Excellence', title: 'Royal Tobacco', subtitle: 'Where tradition meets perfection, crafting moments of pure distinction', cta: 'Explore Our Legacy' },
    about: {
      overline: 'Our Heritage', title: 'The Royal Difference',
      description1: 'Royal Tobacco has established itself as a beacon of excellence in the tobacco industry. With an unwavering commitment to quality and innovation, we have built a legacy that spans generations. Our master blenders combine centuries-old techniques with modern precision to create products that define luxury.',
      description2: 'From our carefully selected tobacco leaves grown in the finest regions of the world to our state-of-the-art facilities in the heart of the Middle East, every step of our process reflects our dedication to delivering an unparalleled experience to our discerning clientele.',
      stats: { employees: '1,200+', employeesLabel: 'Craftsmen', experience: '37+', experienceLabel: 'Years of Mastery', markets: '25+', marketsLabel: 'Countries Served' },
      valuesHeading: 'Our Core Values',
    },
    products: { overline: 'Our Collections', title: 'Distinguished Selection', subtitle: 'Experience the pinnacle of tobacco craftsmanship', cta: 'Discover More' },
    quality: { overline: 'Our Promise', title: 'Uncompromising Excellence', description: 'At Royal Tobacco, quality is not just a standard—it is our obsession. Every leaf is hand-selected from premier tobacco-growing regions. Our facilities employ the most advanced quality control systems, ensuring each product meets our legendary standards.' },
    contact: {
      overline: 'Connect With Us', title: 'Get in Touch', subtitle: 'We welcome your inquiries',
      office: { title: 'Headquarters', address: 'Royal Tower, Business Bay District', city: 'Dubai, United Arab Emirates' },
      phone: { title: 'Telephone', number: '+971-4-555-8800' },
      email: { title: 'Email', address: 'contact@royaltobacco.ae' },
      hours: { title: 'Business Hours', weekdays: 'Sunday – Thursday: 9am – 7pm', weekend: 'Friday – Saturday: 10am – 4pm' },
    },
    footer: { tagline: 'Royal Tobacco — where heritage meets perfection. Crafting the finest tobacco products since 1987.', partOf: 'A Member of', group: 'Royal Holdings International', rights: 'All Rights Reserved' },
    toggleTheme: 'Toggle Theme', toggleLanguage: 'العربية',
  },
  ar: {
    nav: { home: 'الرئيسية', about: 'من نحن', products: 'منتجاتنا', news: 'الأخبار', contact: 'اتصل بنا' },
    hero: { overline: 'تأسست عام 1987 — إرث من التميز', title: 'رويال للتبغ', subtitle: 'حيث يلتقي التراث بالكمال، نصنع لحظات من التميز الفريد', cta: 'اكتشف إرثنا' },
    about: {
      overline: 'تراثنا', title: 'تميز رويال',
      description1: 'رسخت رويال للتبغ مكانتها كمنارة للتميز في صناعة التبغ. بالتزام راسخ بالجودة والابتكار، بنينا إرثاً يمتد عبر الأجيال. يجمع خبراء المزج لدينا بين التقنيات العريقة والدقة الحديثة لابتكار منتجات تجسد الفخامة.',
      description2: 'من أوراق التبغ المختارة بعناية من أفضل المناطق في العالم إلى منشآتنا المتطورة في قلب الشرق الأوسط، كل خطوة في عمليتنا تعكس تفانينا في تقديم تجربة لا مثيل لها لعملائنا المميزين.',
      stats: { employees: '+1,200', employeesLabel: 'حرفي متخصص', experience: '+37', experienceLabel: 'عاماً من الإتقان', markets: '+25', marketsLabel: 'دولة نخدمها' },
      valuesHeading: 'قيمنا الأساسية',
    },
    products: { overline: 'مجموعاتنا', title: 'تشكيلة مميزة', subtitle: 'اختبر قمة الحرفية في صناعة التبغ', cta: 'اكتشف المزيد' },
    quality: { overline: 'وعدنا', title: 'تميز لا يقبل المساومة', description: 'في رويال للتبغ، الجودة ليست مجرد معيار — إنها شغفنا. كل ورقة تُختار يدوياً من أرقى مناطق زراعة التبغ.' },
    contact: {
      overline: 'تواصل معنا', title: 'اتصل بنا', subtitle: 'نرحب باستفساراتكم',
      office: { title: 'المقر الرئيسي', address: 'برج رويال، منطقة الخليج التجاري', city: 'دبي، الإمارات العربية المتحدة' },
      phone: { title: 'الهاتف', number: '+971-4-555-8800' },
      email: { title: 'البريد الإلكتروني', address: 'contact@royaltobacco.ae' },
      hours: { title: 'ساعات العمل', weekdays: 'الأحد – الخميس: 9ص – 7م', weekend: 'الجمعة – السبت: 10ص – 4م' },
    },
    footer: { tagline: 'رويال للتبغ — حيث يلتقي التراث بالكمال. نصنع أجود منتجات التبغ منذ عام 1987.', partOf: 'عضو في', group: 'رويال القابضة الدولية', rights: 'جميع الحقوق محفوظة' },
    toggleTheme: 'تغيير المظهر', toggleLanguage: 'English',
  },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const { siteContent } = useContent();

  // Use API translations when available, otherwise fall back to hardcoded
  const translations = (siteContent?.translations) || defaultTranslations;

  const toggleLanguage = () => setLanguage(prev => prev === 'en' ? 'ar' : 'en');

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  useEffect(() => {
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within a LanguageProvider');
  return context;
};
